import { useMediaBlob } from './useMediaBlob';
import type { MediaRow } from '../../data/dto';

interface Props {
  media: MediaRow;
  /** Whether the e2ee key is available (status === 'ready'). */
  canDecrypt: boolean;
  decrypt: (bytes: Uint8Array) => Uint8Array;
  onOpen: () => void;
  onUnlock: () => void;
  /** Omit to hide the remove (×) button. */
  onDelete?: () => void;
  /** When > 0, shows a small comment badge on the thumbnail. */
  commentCount?: number;
}

/**
 * One gallery thumbnail — fills its grid cell as a square. Tapping the image
 * opens the lightbox; the × removes it (when deletable). Encrypted items show a
 * lock until the key is available. A comment badge surfaces when the photo has
 * comments.
 */
export function MediaTile({ media, canDecrypt, decrypt, onOpen, onUnlock, onDelete, commentCount = 0 }: Props) {
  // thumbnail=true: for a video this is a JPEG poster frame, not the video
  // itself — <img> can render a poster frame but not a video/* blob.
  const { state, url } = useMediaBlob(media, canDecrypt, decrypt, true, true);
  const isVideo = media.media_type === 'video';

  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-surface border border-hairline">
      {state === 'ready' && url && (
        <button onClick={onOpen} className="block size-full" aria-label={isVideo ? 'Play video' : 'View photo'}>
          <img src={url} alt="" className="size-full object-cover" />
        </button>
      )}

      {state === 'loading' && (
        <div className="flex size-full items-center justify-center">
          <span className="size-5 animate-pulse rounded-full bg-grey-soft" />
        </div>
      )}

      {state === 'locked' && (
        <button
          onClick={onUnlock}
          className="flex size-full flex-col items-center justify-center gap-1 text-accent-ink"
          style={{ background: 'color-mix(in oklch, var(--color-accent) 8%, var(--color-bg))' }}
          title="Locked — tap to unlock"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
          <span className="text-[9px] font-bold uppercase tracking-wide">Locked</span>
        </button>
      )}

      {state === 'error' && (
        // Still clickable — this fires for a video with no stored poster
        // thumbnail (e.g. uploaded before this feature); the item itself is
        // fine and the lightbox can still play it.
        <button
          onClick={onOpen}
          className="flex size-full items-center justify-center px-2 text-center text-[10px] font-medium text-ink-3"
          aria-label={isVideo ? 'Play video' : 'View photo'}
        >
          {isVideo ? 'Video' : "Couldn't load"}
        </button>
      )}

      {isVideo && (state === 'ready' || state === 'error') && (
        <div className="pointer-events-none absolute bottom-1.5 right-1.5 flex size-5.5 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-[1px]">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        </div>
      )}

      {commentCount > 0 && (
        <div
          className="pointer-events-none absolute left-1.5 top-1.5 inline-flex items-center gap-0.5 rounded-full bg-ink/75 px-1.5 py-0.5 text-[10px] font-semibold text-bg backdrop-blur-sm"
          title={`${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}`}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.9-.9L3 21l1.9-5.6a8.5 8.5 0 0 1-.9-3.9A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />
          </svg>
          {commentCount}
        </div>
      )}

      {onDelete && (
        <button
          onClick={onDelete}
          title="Remove"
          className="absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-full bg-ink/75 text-bg backdrop-blur-sm"
        >
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M3 3l6 6M9 3L3 9" />
          </svg>
        </button>
      )}
    </div>
  );
}
